import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { awsConfig } from './aws-config';
import Dashboard from './pages/Dashboard';
import Hospitals from './pages/Hospitals';
import Payments from './pages/Payments';
import Settings from './pages/Settings';
import Contracts from './pages/Contracts';
import './App.css';

Amplify.configure(awsConfig);

export const useIsAdmin = (): boolean => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    fetchAuthSession().then(session => {
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) || [];
      setIsAdmin(groups.includes('admin'));
    }).catch(() => setIsAdmin(false));
  }, []);
  return isAdmin;
};

const NavItem: React.FC<{ to: string; icon: React.ReactNode; children: React.ReactNode }> = ({ to, icon, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link to={to} className={`nav-item${isActive ? ' active' : ''}`}>
      <span className="nav-icon">{icon}</span>
      <span className="nav-label">{children}</span>
    </Link>
  );
};

const IconOverview = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
  </svg>
);

const IconHospitals = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const IconPayments = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconContracts = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const IconUsers = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const IconLogOut = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const AppContent: React.FC = () => {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const isAdmin = useIsAdmin();
  const email = user?.signInDetails?.loginId || 'User';
  const initials = email.substring(0, 2).toUpperCase();

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-logo">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" fill="white"/>
              <path d="M2 17l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="sidebar-brand-text">
            <span className="brand-name">OmidNetCare</span>
            <span className="brand-sub">Finance</span>
          </div>
        </div>

        <div className="sidebar-section-label">Navigation</div>

        <nav className="sidebar-nav">
          <NavItem to="/" icon={<IconOverview />}>Overview</NavItem>
          <NavItem to="/hospitals" icon={<IconHospitals />}>Hospitals</NavItem>
          <NavItem to="/payments" icon={<IconPayments />}>Payments</NavItem>
          <NavItem to="/contracts" icon={<IconContracts />}>Contracts</NavItem>
          {isAdmin && <NavItem to="/settings" icon={<IconUsers />}>Users</NavItem>}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar-circle">{initials}</div>
            <div className="user-details">
              <span className="user-name-text">{email.split('@')[0]}</span>
              <span className="user-role-badge">{isAdmin ? 'Admin' : 'Staff'}</span>
            </div>
          </div>
          <button onClick={signOut} className="btn-signout" title="Sign out">
            <IconLogOut />
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/hospitals" element={<Hospitals />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

const formFields = {
  signIn: {
    username: {
      placeholder: 'Enter your email',
      label: 'Email',
      inputProps: { type: 'email', required: true },
    },
  },
  forceNewPassword: {
    password: {
      placeholder: 'Create a strong password',
      label: 'New Password',
    },
  },
};

const components = {
  Header() {
    return (
      <div className="auth-header">
        <div className="auth-logo">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5z" fill="white"/>
            <path d="M2 17l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h1 className="auth-title">OmidNetCare Finance</h1>
        <p className="auth-subtitle">Hospital Subscription Management</p>
      </div>
    );
  },
};

const App: React.FC = () => {
  return (
    <Authenticator formFields={formFields} components={components} hideSignUp>
      {() => (
        <Router>
          <AppContent />
        </Router>
      )}
    </Authenticator>
  );
};

export default App;
